import { useNavigate } from "react-router-dom";

import { Models, OrderDeliveryStatus, OrderPickupStatus } from "api/types";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { formatToSimpleDate } from "utils/dayjsUtils";

export interface RetailProductPastOrderLineItemCardProps {
  thumbnailImageUri?: string;
  name: string;
  totalQuantity: number;
  status: OrderPickupStatus | OrderDeliveryStatus;
  redeemedOnUtc?: string;
  estimatedDeliveryDateAtUtc?: string;
  shipmentTrackingUrl?: string;
  orderId?: string; // Made optional for now to use for earned products
  packageId: string;
  packageListingId: string;
  retailProductRefunds: Models<"DisplayRetailProductRefundSupportDetailsForOrder">[];
}

export const RetailProductPastOrderLineItemCard = ({
  thumbnailImageUri,
  name,
  totalQuantity,
  status,
  redeemedOnUtc,
  estimatedDeliveryDateAtUtc,
  shipmentTrackingUrl,
  orderId,
  packageId,
  packageListingId,
  retailProductRefunds,
}: RetailProductPastOrderLineItemCardProps) => {
  const navigate = useNavigate();

  const refundDetails = orderId
    ? retailProductRefunds?.find(
        (refund) =>
          refund.orderId.value === orderId &&
          refund.items.some(
            (item) =>
              item.retailProductVariantId.value === packageId &&
              item.retailProductListingId.value === packageListingId
          )
      )?.items
    : undefined;

  const handleClick = () => {
    if (orderId) {
      // Regular order - navigate to order details
      navigate(
        `/wallet/products/order/${orderId}?type=past&itemId=${packageId}`
      );
    } else {
      // Earned product - navigate to earned product details
      navigate(`/wallet/products/earned/${packageId}?type=earned`);
    }
  };

  const renderStatus = () => {
    if (refundDetails) {
      return `Refund requested ${formatToSimpleDate(
        refundDetails[refundDetails.length - 1].initiatedAtUtc
      )}`;
    }
    if (shipmentTrackingUrl) {
      return <span className="text-left text-body3">Shipped</span>;
    }
    if (
      status === OrderDeliveryStatus.Pending &&
      !shipmentTrackingUrl &&
      !redeemedOnUtc
    ) {
      return <span className="text-left text-body3">Pending Shipment</span>;
    }
    if (status === OrderPickupStatus.PickedUp && redeemedOnUtc) {
      return (
        <span className="text-left text-body3">
          Picked up {formatToSimpleDate(redeemedOnUtc)}
        </span>
      );
    }
    if (
      status === OrderDeliveryStatus.Delivered &&
      estimatedDeliveryDateAtUtc
    ) {
      return (
        <span className="text-left text-body3">
          Delivered {formatToSimpleDate(estimatedDeliveryDateAtUtc)}
        </span>
      );
    }
    return null;
  };

  return (
    <button
      className="relative flex w-full flex-col rounded-lg border bg-white p-4 shadow-sm transition-all duration-200 hover:shadow-md active:scale-[0.98]"
      onClick={handleClick}
    >
      <div className="flex flex-row items-center gap-3">
        <div className="relative">
          {thumbnailImageUri && (
            <img
              src={thumbnailImageUri}
              alt={name}
              className="h-[60px] w-[60px] rounded-lg border object-cover"
            />
          )}
          {!thumbnailImageUri && (
            <div className="h-[60px] w-[60px] rounded-lg border bg-white" />
          )}
          {totalQuantity > 1 && (
            <div className="absolute -right-2 -top-2">
              <DescriptiveTag color="primary" size="xsmall">
                {totalQuantity.toString()}
              </DescriptiveTag>
            </div>
          )}
        </div>
        <div className="flex flex-1 flex-col">
          <p className="mb-1 text-left font-serif text-body2">{name}</p>
          <p className="text-left text-body3">{renderStatus()}</p>
        </div>
        <div className="flex items-center">
          <span className="text-body3 text-brand-color/70">View Details</span>
        </div>
      </div>
    </button>
  );
};
